import store from '@/state/store'
import { Helper } from '@/mixin/helper'
import { hasLineOrderFeatureAccess } from '@/helpers/tms-line-order'

import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as Vendor from '@/services/rest-api/lib/vendor/vendor'
import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Product from '@/services/rest-api/lib/route-optimization/product'
import * as Delivery from '@/services/rest-api/lib/delivery/delivery-order'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

const hasAccessAll = store.getters['identity/hasAccessAll']
const hasTmsUserGroupAccess = hasAccessAll(['tms-user-group:read']) && Helper.methods.isSuperUser()
const hasTmsColdChainAccess = hasAccessAll(['tms-cold-chain:read'])
const hasLineOrderAccess = hasLineOrderFeatureAccess()

const formatListPreview = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return '-'
  if (list.length <= 2) return list.join(', ')
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`
}

const getLineOrderData = async (opts = {}) => {
  const response = await Delivery.getLineOrder(opts)
  const paginatedResult = response?.data?.paginated_result || []

  return {
    metadata: {
      ...(response?.metadata || {}),
    },
    data: {
      ...(response?.data || {}),
      paginated_result: paginatedResult.map((item) => ({
        ...item,
        id: item.line_order,
        line_order_label: item.line_order,
        delivery_order_count: item.delivery_order_count,
        shipped_customers: item.customer_names,
        created_by: item.created_bys,
      })),
    },
    isPagination: true,
  }
}

export const tableFilterFields = (t) => [
  {
    name: 'created_on',
    isShow: true,
    label: t('delivery.fields.createdDate'),
    type: 'date-picker',
    datepickerType: 'datetime',
    valueType: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    format: 'DD MMM YYYY HH:mm',
    titleFormat: 'DD MMM YYYY',
    rangeSeparator: ' - ',
    range: true,
    confirm: true,
    confirmText: t('global.apply'),
    disabledDate: (date) => {
      return date > new Date()
    },
    shortcuts: [
      {
        text: t('global.time.today'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('global.time.yesterday'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 1)
          startDate.setHours(0, 0, 0)

          endDate.setDate(endDate.getDate() - 1)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('delivery.filter.last3Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 3)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('delivery.filter.last7Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 7)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
    ],
  },
  {
    name: 'expected_delivered_on',
    isShow: true,
    label: t('delivery.fields.estimatedDelivered'),
    type: 'date-picker',
    datepickerType: 'datetime',
    valueType: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
    format: 'DD MMM YYYY HH:mm',
    titleFormat: 'DD MMM YYYY',
    rangeSeparator: ' - ',
    range: true,
    confirm: true,
    confirmText: t('global.apply'),
    shortcuts: [
      {
        text: t('global.time.today'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('global.time.yesterday'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 1)
          startDate.setHours(0, 0, 0)

          endDate.setDate(endDate.getDate() - 1)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('delivery.filter.last3Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 3)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
      {
        text: t('delivery.filter.last7Days'),
        onClick: () => {
          const startDate = new Date()
          const endDate = new Date()

          startDate.setDate(startDate.getDate() - 7)
          startDate.setHours(0, 0, 0)
          endDate.setHours(23, 59, 59)

          return [startDate, endDate]
        },
      },
    ],
  },
  {
    name: 'shipment_type',
    isShow: true,
    label: t('delivery.fields.deliveryType'),
    type: 'button-group',
    placeholder: t('delivery.placeholder.deliveryType'),
  },
  {
    name: 'line_order',
    isShow: hasLineOrderAccess,
    label: t('delivery.fields.lineOrder'),
    type: 'value-help-v2',
    placeholder: t('delivery.placeholder.lineOrder'),
    trackBy: 'id',
    displayedBy: 'line_order_label',
    listGetter: getLineOrderData,
    multiple: true,
    visibleTagCount: 2,
    canSelectAll: true,
    tableFields: [
      {
        label: t('delivery.fields.lineOrder'),
        key: 'line_order',
        isSortable: true,
      },
      {
        label: t('delivery.fields.totalDelivery'),
        key: 'delivery_order_count',
        isSortable: true,
      },
      {
        label: t('delivery.fields.shippedCustomerName'),
        key: 'shipped_customers',
        isSortable: false,
        formatValue: (value) => formatListPreview(value),
      },
      {
        label: t('delivery.fields.createdBy'),
        key: 'created_by',
        isSortable: false,
        formatValue: (value) => formatListPreview(value),
      },
    ],
  },
  {
    name: 'user_group_id',
    isShow: hasTmsUserGroupAccess,
    label: t('delivery.fields.userGroup'),
    type: 'value-help-v2',
    placeholder: t('delivery.action.selectUserGroup'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: true,
    tableFields: [
      {
        label: t('delivery.fields.userGroup'),
        key: 'name',
        isSortable: false,
      },
      {
        label: t('delivery.fields.vehicleGroup'),
        key: 'vgroups',
        isSortable: false,
        formatter: (value) => {
          return value?.length != null
            ? value.length === 0
              ? t('delivery.fields.allVehicleGroup')
              : value.length
            : '-'
        },
      },
    ],
  },
  {
    name: 'shipped_customer_id',
    isShow: true,
    label: t('delivery.fields.shippedCustomerName'),
    type: 'value-help-v2',
    placeholder: t('delivery.placeholder.shippedCustomer'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    multiple: true,
    tableFields: [
      {
        label: t('delivery.fields.customerName'),
        key: 'name',
        isSortable: true,
      },
    ],
  },
  {
    name: 'product_category',
    isShow: true,
    label: t('delivery.fields.productCategory'),
    type: 'value-help-v2',
    placeholder: t('delivery.placeholder.productCategory'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Product.getProductCategory(),
    multiple: true,
    canSelectAll: true,
    tableFields: [
      {
        label: t('delivery.fields.productCategory'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },
  {
    name: 'vendor_id',
    isShow: hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']),
    label: t('delivery.fields.vendor'),
    type: 'value-help-v2',
    placeholder: t('delivery.action.selectVendor'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Vendor.get,
    multiple: true,
    tableFields: [
      {
        label: t('delivery.fields.vendorName'),
        key: 'name',
        isSortable: true,
      },
    ],
  },
  {
    name: 'origin_address_id',
    isShow: true,
    label: t('delivery.fields.originAddress'),
    type: 'value-help-v2',
    placeholder: t('delivery.placeholder.originAddress'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Address.get(),
    multiple: true,
    tableFields: [
      {
        label: t('master.form.address.name'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
      {
        label: t('master.form.address.city'),
        key: 'city',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },
  {
    name: 'destination_address_id',
    isShow: true,
    label: t('delivery.fields.destinationAddress'),
    type: 'value-help-v2',
    placeholder: t('delivery.placeholder.destinationAddress'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Address.get(),
    multiple: true,
    tableFields: [
      {
        label: t('master.form.address.name'),
        key: 'name',
        isSortable: true,
        isRedirectable: true,
      },
      {
        label: t('master.form.address.city'),
        key: 'city',
        isSortable: true,
        isRedirectable: true,
      },
    ],
  },
  {
    type: 'range-input',
    isShow: true,
    label: t('delivery.fields.loadWeight'),
    min: {
      name: 'load_weight_min',
      label: t('global.field.minimum'),
      type: 'number',
      validation: 'bail|number|min:0,value',
      suffix: 'Kg',
      'validation-behavior': 'live',
    },
    max: {
      name: 'load_weight_max',
      label: t('global.field.maximum'),
      type: 'number',
      validation: 'bail|number|min:0,value',
      suffix: 'Kg',
      'validation-behavior': 'live',
    },
  },
  {
    type: 'range-input',
    isShow: true,
    label: t('delivery.fields.loadVolume'),
    min: {
      name: 'load_volume_min',
      label: t('global.field.minimum'),
      type: 'number',
      validation: 'bail|number|min:0,value',
      suffix: 'Cbm',
      'validation-behavior': 'live',
    },
    max: {
      name: 'load_volume_max',
      label: t('global.field.maximum'),
      type: 'number',
      validation: 'bail|number|min:0,value',
      suffix: 'Cbm',
      'validation-behavior': 'live',
    },
  },
  {
    type: 'range-input',
    isShow: hasTmsColdChainAccess,
    label: t('delivery.filter.temperature'),
    min: {
      name: 'load_min_temperature',
      label: t('global.field.minimum'),
      type: 'number',
      validation: 'bail|number',
      suffix: '°C',
      'validation-behavior': 'live',
    },
    max: {
      name: 'load_max_temperature',
      label: t('global.field.maximum'),
      type: 'number',
      validation: 'bail|number',
      suffix: '°C',
      'validation-behavior': 'live',
    },
  },
]
